import { initiateSearch } from '../utils/search/helpers'
import { getLocaleFromPath, prependLocale } from '../utils/localeHelpers'
import { initLazyLoadedImages } from './listing_tile_rendering/listingImageRenderer'
import { getWishlistedItemsFromPage, getHitHTML, getImageOnlyHitHTML } from './listing_tile_rendering/listingTileUtils'

import setUpAddToWishlistBehaviour from './addToWishlist'
import { setOpenModalButtons } from './modals'
import setMaskedUrls from './maskedUrls'
import { bindCarouselImpression, bindRecentItemsCarouselImpressionEvents } from './analytics'
import { registerNewImpressions } from '../analytics/utils/impressions'
import { getListingImpressionsSchema } from '../analytics/AnalyticsHelpers'
import { getCompleteTheRoomQuery } from './completeRoomQuery'
import { all } from '../utils/search/filterSyntax'

export default function initHomepageListingCarousels () {
  const cache = {}
  const carouselPromises = [
    initNewListingsSections(cache),
    initValuePiecesCarousel(cache),
    initPremiumPiecesCarousel(cache),
    initCompleteTheRoomSection()
  ]

  // allSettled waits for promises to be concluded, regardless of success or failure
  Promise.allSettled(carouselPromises).then(() => {
    initLazyLoadedImages()
    setOpenModalButtons()
    setUpAddToWishlistBehaviour()
    setMaskedUrls()
    bindRecentItemsCarouselImpressionEvents()
  })
}

// The premium/value/new-in carousels are identical for every visitor, so all
// three are read from one shared, cached endpoint instead of a live query each,
// fetched once and only when one of them is on the page.
function cachedCarousels (cache) {
  cache.response = cache.response || fetchCachedCarousels()
  return cache.response
}

function fetchCachedCarousels () {
  return window.fetch(
    prependLocale('/homepage_carousels.json', getLocaleFromPath(window.location.pathname)),
    { headers: { Accept: 'application/json' } }
  ).then(response => (response.ok ? response.json() : {})).catch(() => ({}))
}

function initNewListingsSections (cache) {
  const newListingsSection = document.querySelector('.vjs-new-listings')
  if (newListingsSection) {
    return loadCarousel(newListingsSection, cachedCarousels(cache).then(data => data.new_in || { hits: [] }), 'sold:false AND published_recently:true', 'homepageNewListings')
  }
  return Promise.resolve()
}

// Reused in Homepage, PDP and Order confirmation pages
export function initCompleteTheRoomSection () {
  const completeTheRoomSection = document.querySelector('.vjs-complete-the-room')
  if (completeTheRoomSection) {
    const { productType, taxonomyTranslations } = completeTheRoomSection.dataset
    const translations = taxonomyTranslations ? JSON.parse(taxonomyTranslations) : null
    // The only homepage carousel that actually queries an engine — the other
    // three read a shared cached endpoint and pass their filter string purely as
    // an analytics label, so those stay in Algolia syntax on both engines.
    const query = all(['sold:false', getCompleteTheRoomQuery(productType, translations)])
    return loadCarousel(completeTheRoomSection, initiateSearch(query), query, 'completeTheRoom')
  }
  return Promise.resolve()
}

function initValuePiecesCarousel (cache) {
  const valuePiecesCarousel = document.querySelector('.vjs-value-pieces-carousel')
  if (valuePiecesCarousel) {
    return loadCarousel(valuePiecesCarousel, cachedCarousels(cache).then(data => data.value || { hits: [] }), 'sold:false AND commercially_accessible:true', 'homepageValuePieces')
  }
  return Promise.resolve()
}

function initPremiumPiecesCarousel (cache) {
  const premiumPiecesCarousel = document.querySelector('.vjs-premium-pieces-carousel')
  if (premiumPiecesCarousel) {
    return loadCarousel(premiumPiecesCarousel, cachedCarousels(cache).then(data => data.premium || { hits: [] }), 'sold:false AND luxury_items:true', 'homepagePremiumPieces')
  }
  return Promise.resolve()
}

function loadCarousel (placeholder, resultsPromise, searchQuery, carouselFunction) {
  return resultsPromise.then(results => {
    const cappedHits = results.hits.slice(0, 20)

    const queryID = results.queryID ? results.queryID : ''
    cappedHits.forEach((hit, index) => {
      hit.__position = index + 1
      hit.__queryID = queryID
    })

    const fullSizeTiles = JSON.parse(placeholder.getAttribute('data-full-size-tiles'))
    const wishlistedItems = fullSizeTiles ? getWishlistedItemsFromPage() : []
    const isTradeBuyer = JSON.parse(placeholder.getAttribute('data-trade-buyer'))
    const isLoggedIn = JSON.parse(document.querySelector('body').getAttribute('data-logged-in'))
    const showCategoryLink = JSON.parse(placeholder.getAttribute('data-show-category-link'))
    const userStatus = { isTradeBuyer, isLoggedIn }
    const tileOptions = { showViewSimilar: false, showCategoryLink: showCategoryLink }
    const carouselTiles = fullSizeTiles
      ? cappedHits.map(hit => getHitHTML(hit, wishlistedItems, userStatus, tileOptions))
      : cappedHits.map(hit => getImageOnlyHitHTML(hit, tileOptions))
    placeholder.innerHTML = carouselTiles.join('')
    bindCarouselImpression(placeholder)

    registerNewImpressions(getListingImpressionsSchema(cappedHits, searchQuery, 0))
    return import(/* webpackChunkName: "carousels" */ './carousels').then(module => module[carouselFunction]())
  })
}
